import React from "react";
import ReactDOM from "react-dom/client";
import { call, delay, put, select, takeEvery } from "redux-saga/effects";

import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import {
  AuthActions,
  authActions,
  GdasScopedClaim,
  GdasSeller,
  GdasTenant,
  getActiveTenantId,
  getAvailableTenants,
  getGdasClaims,
  getGdasSellersList,
  getIsUserSignedIn,
  getMarketInfo,
  getUser,
  MarketInfo,
  NavigationActions,
  UserInfo,
} from "@gamedev-web/gamedev-sdk-core";
import { getIsDark, UhfActions } from "@gamedev-web/gamedev-uhf";
import {
  GameDevAuthDirectoryPicker,
  GameDevAuthMeControl,
  GameDevAuthProvider,
} from "@gaming/gdas-react";
import { MessageProvider, MessageProviderProps } from "@messageformat/react";

import { LoginButton } from "../../components/LoginButton/LoginButton";
import { getResourceStringsAsync } from "../../localization/getResourceStringsAsync";

const siteLocationPrefix = "/games/partner";
const signInUrl = `${siteLocationPrefix}/auth/login`;
const signOutUrl = `${siteLocationPrefix}/auth/logout`;
const localePrefixRegex = /^\/[a-z]{2,3}-[a-z]{2,4}(?=\/|$)/i;

export const getSignInCallbackPath = (pathname: string) => {
  const localePrefix = pathname.match(localePrefixRegex)?.[0] ?? "";
  const pathnameWithoutLocale = pathname.slice(localePrefix.length) || "/";
  const isLandingPath = [
    "/",
    "/games",
    "/games/",
    siteLocationPrefix,
    `${siteLocationPrefix}/`,
  ].includes(pathnameWithoutLocale);

  return isLandingPath ? `${localePrefix}${siteLocationPrefix}/dashboard` : pathname;
};

/** Build a login URL that preserves tenantId from the current page URL if present */
const getSignInUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get("tenantId") || params.get("tenantid");
  // Build callback with all non-tenant query params preserved
  const callbackParams = new URLSearchParams(params);
  callbackParams.delete("tenantId");
  callbackParams.delete("tenantid");
  const callbackQuery = callbackParams.toString();
  const callbackPath = `${getSignInCallbackPath(window.location.pathname)}${
    callbackQuery ? `?${callbackQuery}` : ""
  }`;

  if (tenantId) {
    return `${signInUrl}?tenantId=${tenantId}&callbackUrl=${encodeURIComponent(callbackPath)}`;
  }
  return `${signInUrl}?callbackUrl=${encodeURIComponent(callbackPath)}`;
};

const handleTenantChange = (tenantId: string) => {
  // Redirect to server login with the new tenant — this acquires a new OBO token
  // Store the selected tenantId in localStorage so we can restore it after page reload
  localStorage.setItem("GDAS_PENDING_TENANT", tenantId);
  const currentPath = window.location.pathname;
  window.location.href = `${signInUrl}?tenantId=${tenantId}&callbackUrl=${encodeURIComponent(
    currentPath
  )}`;
};

/** Redirect to login with common authority (no tenant hint) */
const redirectToCommonLogin = () => {
  localStorage.removeItem("GDAS_PENDING_TENANT");
  localStorage.removeItem("GDAS_CACHED_TENANTS");
  const callbackPath = window.location.pathname;
  window.location.href = `${signInUrl}?callbackUrl=${encodeURIComponent(callbackPath)}`;
};

const hasTenants = (tenants: GdasTenant[] | undefined | null): tenants is GdasTenant[] =>
  Array.isArray(tenants) && tenants.length > 0;

type WindowType = {
  msCommonShell: {
    load: (_props: Record<string, unknown>) => void;
  };
};

let testEnv = false;
export const setTestEnv = (value: boolean) => {
  testEnv = value;
};

export function* populateUserInfo(action: { type: string; initialClientSetup?: boolean }) {
  const isGdasUpdate =
    action.type === AuthActions.UPDATE_GDAS_PROFILE ||
    action.type === AuthActions.SET_ACTIVE_TENANT;
  const isThemeChange =
    action.type === UhfActions.SET_DARK || action.type === UhfActions.RESET_DARK;
  const loggedIn: boolean | undefined = yield select(getIsUserSignedIn);
  if (loggedIn && !action.initialClientSetup && !isGdasUpdate && !isThemeChange) {
    return;
  }

  const browserWindow: WindowType = window as unknown as WindowType;

  try {
    yield delay(500);
    const userInfo: UserInfo = yield select(getUser);

    const uhfContainer = document.querySelector(".c-uhfh-actions");
    if (!uhfContainer) {
      return;
    }

    const existingControl = uhfContainer.querySelector("#meControl");
    if (existingControl) {
      if (existingControl.classList.contains("gdas-root") && !isGdasUpdate && !isThemeChange) {
        // Our control already exists and this is not a GDAS data or theme update — skip.
        return;
      }
      // Remove existing control to re-render (either native UHF or stale GDAS control)
      existingControl.remove();
    }

    const containerDiv = document.createElement("div");
    containerDiv.setAttribute("id", "meControl");
    containerDiv.setAttribute("class", "gdas-root");
    const root = ReactDOM.createRoot(uhfContainer.appendChild(containerDiv));

    // Read GDAS data from Redux state
    const tenants: GdasTenant[] = yield select(getAvailableTenants);
    const activeTenantId: string | undefined = yield select(getActiveTenantId);
    const sellers: GdasSeller[] = yield select(getGdasSellersList);
    const claimsData: { scopedClaims: GdasScopedClaim[] } | undefined = yield select(getGdasClaims);
    const isDark: boolean = yield select(getIsDark);

    if (userInfo) {
      if (testEnv || browserWindow /* .msCommonShell */) {
        if (!testEnv) {
          root.render(
            React.createElement(
              FluentProvider,
              { theme: isDark ? webDarkTheme : webLightTheme },
              React.createElement(
                GameDevAuthProvider,
                {
                  config: {
                    clientId: "b43d62f4-3cfb-433d-8e28-69da90f843cf",
                    partnerId: "playfab",
                    theme: isDark ? ("dark" as const) : ("light" as const),
                    account: {
                      username: userInfo.emailAddress,
                      name: userInfo.displayName as string,
                      picture: userInfo.avatarImage,
                      tenantId: activeTenantId || "",
                    },
                    ...(hasTenants(tenants) && { tenants }),
                    ...(sellers.length > 0 && { sellers }),
                    ...(claimsData?.scopedClaims && { claims: claimsData.scopedClaims }),
                    ...(activeTenantId && { tenantId: activeTenantId }),
                  },
                },
                React.createElement(GameDevAuthMeControl, {
                  onSignIn: () => (window.location.href = getSignInUrl()),
                  onSignOut: () => {
                    localStorage.removeItem("GDAS_PENDING_TENANT");
                    localStorage.removeItem("GDAS_CACHED_TENANTS");
                    window.location.href = signOutUrl;
                  },
                  onSwitchTo: () => {
                    localStorage.removeItem("GDAS_PENDING_TENANT");
                    localStorage.removeItem("GDAS_CACHED_TENANTS");
                    window.location.href = getSignInUrl();
                  },
                  // When no tenants, override "Switch Directory" to redirect to common login
                  ...(!hasTenants(tenants) && {
                    commands: [{ text: "Switch directory", onClick: redirectToCommonLogin }],
                  }),
                }),
                // Render DirectoryPicker when tenants are available
                ...(hasTenants(tenants)
                  ? [
                      React.createElement(GameDevAuthDirectoryPicker, {
                        key: "dir-picker",
                        closeOnSwitchTenant: true,
                        onTenantChange: handleTenantChange,
                      }),
                    ]
                  : [])
              )
            )
          );
        }
      }
      // Only dispatch updateUser on initial setup, not on GDAS data refreshes
      if (!isGdasUpdate) {
        yield put(authActions.updateUser(userInfo));
      }
    } else {
      const marketInfo: MarketInfo = yield select(getMarketInfo);
      const baseStrings: Record<string, string> = yield call(getResourceStringsAsync, marketInfo);
      const loginElement = React.createElement(LoginButton, {
        onSignIn: () => (window.location.href = getSignInUrl()),
      });
      root.render(
        React.createElement(MessageProvider, {
          messages: { baseStrings },
          children: loginElement,
        } as MessageProviderProps)
      );
      yield put(authActions.updateUser(undefined));
    }
  } catch (e) {
    // TODO: telemetry and error handling.
    yield put(authActions.updateUser(undefined));
  }
}

export function* authSagas() {
  yield takeEvery(AuthActions.CHECK_AUTH_STATUS, populateUserInfo);
  yield takeEvery(NavigationActions.LOCATION_CHANGE, populateUserInfo);
  // Re-render when GDAS data arrives so the DirectoryPicker gets tenant/seller data
  yield takeEvery(AuthActions.UPDATE_GDAS_PROFILE, populateUserInfo);
  // Re-render when user switches tenant via DirectoryPicker
  yield takeEvery(AuthActions.SET_ACTIVE_TENANT, populateUserInfo);
  // Re-render MeControl when dark mode changes so it picks up the new theme
  yield takeEvery(UhfActions.SET_DARK, populateUserInfo);
  yield takeEvery(UhfActions.RESET_DARK, populateUserInfo);
  // We need to do the initial auth status check. After that, location changes within the
  // redux-router will check it.
  yield put({ type: AuthActions.CHECK_AUTH_STATUS, initialClientSetup: true });
}
